import { FormEvent, useState } from 'react';

interface Validation {
  required?: {
    value: boolean;
    message: string;
  };
  pattern?: {
    value: string;
    message: string;
  };
  custom?: {
    isValid: (value: string) => boolean;
    message: string;
  };
}

type ErrorRecord<T> = Partial<Record<keyof T, string>>;

type Validations<T extends {}> = Partial<Record<keyof T, Validation>>;

export const useForm = <T extends Record<keyof T, any> = {}>(options?: {
  validations?: Validations<T>;
  initialValues?: Partial<T>;
  onSubmit?: () => void;
}) => {
  const [data, setData] = useState<T>((options?.initialValues || {}) as T);
  const [errors, setErrors] = useState<ErrorRecord<T>>({});

  const handleChange = (
    key: keyof T,
    value?: string | number,
    secondaryData?: Partial<T>
  ) => {
    const newData = {
      ...data,
      ...secondaryData,
      [key]: value,
    };
    setData(newData);
    const { keyValid, keyNewErrors } = validateKey(key, newData);
    if (keyValid) setErrors({ ...errors, [key]: undefined });
    else setErrors({ ...errors, ...keyNewErrors });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validations = options?.validations;
    if (validations) {
      let valid = true;
      let newErrors: ErrorRecord<T> = {};
      for (const key in validations) {
        const { keyValid, keyNewErrors } = validateKey(key, data);
        if (!keyValid) valid = false;
        if (keyNewErrors) newErrors = { ...newErrors, ...keyNewErrors };
      }

      if (!valid) {
        setErrors(newErrors);
        return;
      }
    }

    setErrors({});
    if (options?.onSubmit) options.onSubmit();
  };

  const validateKey = (key: keyof T, checkData: T) => {
    let valid = true;
    let newErrors: ErrorRecord<T> = {};

    const value = checkData[key];
    const validations = options?.validations;
    const validation = validations && validations[key];
    const pattern = validation?.pattern;
    const custom = validation?.custom;
    if (validation?.required?.value && !value) {
      valid = false;
      newErrors[key] = validation?.required?.message;
    }

    if (pattern?.value && !RegExp(pattern.value).test(value)) {
      valid = false;
      newErrors[key] = pattern.message;
    }

    if (custom?.isValid && !custom.isValid(value)) {
      valid = false;
      newErrors[key] = custom.message;
    }

    return { keyValid: valid, keyNewErrors: newErrors };
  };

  return {
    data,
    handleChange,
    handleSubmit,
    errors,
  };
};
